import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import confetti from 'canvas-confetti';
import { safeString } from '../../utils/safeString';
import DotsAndBoxesBoard from './DotsAndBoxesBoard';
import {
  createInitialGameState,
  isValidMove,
  makeMove,
  getGameStatus,
} from './logic';
import {
  subscribeToGame,
  updateGameState,
  updateGameStatus,
  requestRematch,
  resetGameForRematch,
  saveGameToHistory,
  updateMatchStats,
  deleteGame,
  checkBothRematchRequested,
} from '../../services/gameService';
import { addRecentOpponent } from '../../services/friendService';
import { leaveRoom } from '../../services/roomService';
import { database } from '../../services/firebase';
import { ref, update, serverTimestamp } from 'firebase/database';
import './DotsAndBoxes.css';

const DotsAndBoxes = ({ roomCode, roomData, user }) => {
  console.log('DotsAndBoxes rendering:', { roomCode, roomData, user });

  const navigate = useNavigate();
  const [gameState, setGameState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rematchRequested, setRematchRequested] = useState(false);
  const [opponentRematchRequested, setOpponentRematchRequested] = useState(false);
  const [error, setError] = useState(null);
  const confettiShownRef = useRef(false);
  const gameRecordedRef = useRef(false);

  // Validate props
  if (!roomCode || !roomData || !user) {
    console.error('Missing required props:', { roomCode, roomData, user });
    return (
      <div className="dotsandboxes-container">
        <div className="error-message">Missing required data. Please try again.</div>
      </div>
    );
  }

  const isHost = roomData.host && roomData.host.userId === user.uid;
  const opponent = isHost ? roomData.guest : roomData.host;

  // If opponent left, show waiting state
  if (!opponent) {
    const handleReturnToLobby = async () => {
      try {
        const roomRef = ref(database, `rooms/${roomCode}`);
        await update(roomRef, {
          status: 'waiting',
          gameSelected: null,
          lastActivity: serverTimestamp()
        });
        navigate(`/room/${roomCode}`);
      } catch (error) {
        console.error('Error returning to lobby:', error);
        navigate(`/room/${roomCode}`);
      }
    };

    return (
      <div className="dotsandboxes-container">
        <div className="game-header">
          <h2>Dots & Boxes</h2>
        </div>
        <div className="player-waiting-state">
          <div className="waiting-spinner"></div>
          <h3>Player left the room</h3>
          <p>Waiting for player to return...</p>
          <p className="waiting-hint">The game will resume when they come back</p>
          <button className="leave-button" onClick={handleReturnToLobby}>
            Return to Lobby
          </button>
        </div>
      </div>
    );
  }

  console.log('Player info:', { isHost, opponent });

  // Subscribe to game updates
  useEffect(() => {
    console.log('Setting up game subscription for room:', roomCode);

    try {
      const unsubscribe = subscribeToGame(roomCode, async (game) => {
        console.log('Game update received:', game);

        if (game && game.gameState) {
          console.log('Setting gameState to:', game.gameState);
          setGameState(game.gameState);

          // Check rematch requests
          if (game.rematchRequests) {
            const myRequest = !!game.rematchRequests[user.uid];
            const opponentRequest = !!game.rematchRequests[opponent.userId];

            setRematchRequested(myRequest);
            setOpponentRematchRequested(opponentRequest);

            // If both players requested rematch, only the host resets the game
            if (myRequest && opponentRequest && isHost && game.gameState.isGameOver) {
              console.log('Both players requested rematch, host is resetting game...');

              const newGameState = createInitialGameState(
                roomData.host.userId,
                roomData.guest.userId
              );

              await resetGameForRematch(
                roomCode,
                'dotsandboxes',
                newGameState,
                {
                  host: roomData.host,
                  guest: roomData.guest,
                }
              );
            }
          } else {
            setRematchRequested(false);
            setOpponentRematchRequested(false);
          }
        } else if (game === null) {
          console.warn('Game not found in database');
          setError('Game not found. It may not have been created yet.');
        } else if (game && !game.gameState) {
          console.error('Game exists but has no gameState property');
          setError('Game data is missing gameState. Please try restarting the game.');
        }

        setLoading(false);
      });

      return () => {
        console.log('Cleaning up game subscription');
        unsubscribe();
      };
    } catch (err) {
      console.error('Error setting up game subscription:', err);
      setError(err.message);
      setLoading(false);
    }
  }, [roomCode, user.uid, opponent.userId, isHost, roomData]);

  // Trigger confetti on win (only once per game)
  useEffect(() => {
    if (gameState && gameState.isGameOver && gameState.winner === user.uid && !confettiShownRef.current) {
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
      });
      confettiShownRef.current = true;
    }

    // Reset the flags when game restarts (not game over anymore)
    if (gameState && !gameState.isGameOver) {
      confettiShownRef.current = false;
      gameRecordedRef.current = false;
    }
  }, [gameState, user.uid]);

  // Save game to history when it ends (only once)
  useEffect(() => {
    if (gameState && gameState.isGameOver && !gameRecordedRef.current) {
      gameRecordedRef.current = true;

      const iWon = gameState.winner === user.uid;
      const result = gameState.winner === 'draw' ? 'draw' : iWon ? 'won' : 'lost';

      // Save to game history
      saveGameToHistory(user.uid, {
        gameType: 'dotsandboxes',
        opponent: safeString(opponent?.displayName, 'Opponent'),
        opponentId: opponent.userId,
        result,
        winner: gameState.winner,
        roomCode,
      });

      // Update match stats
      updateMatchStats(user.uid, 'dotsandboxes', result);

      // Add to recent opponents
      addRecentOpponent(user.uid, {
        userId: opponent.userId,
        displayName: safeString(opponent?.displayName, 'Opponent'),
        photoURL: safeString(opponent?.photoURL, ''),
        lastGame: 'dotsandboxes',
        lastResult: result,
      });

      // Update game status
      updateGameStatus(roomCode, 'completed');
    }
  }, [gameState, user.uid, opponent, roomCode]);

  const handleLineClick = async (type, row, col) => {
    if (!gameState || gameState.isGameOver) return;

    // Check if it's my turn
    if (gameState.currentTurn !== user.uid) {
      return;
    }

    // Validate move
    if (!isValidMove(gameState, type, row, col)) {
      return;
    }

    // Make move
    const newGameState = makeMove(gameState, type, row, col, user.uid);

    // Update Firebase
    await updateGameState(roomCode, newGameState);
  };

  const handleRematch = async () => {
    // Just request the rematch - the subscription will handle the reset
    await requestRematch(roomCode, user.uid);
    // Local state will be updated by the subscription
  };

  const handleLeaveGame = async () => {
    try {
      // Update room status back to waiting and clear game selections
      const roomRef = ref(database, `rooms/${roomCode}`);
      await update(roomRef, {
        status: 'waiting',
        gameSelected: null,
        'host/gameSelection': null,
        'guest/gameSelection': null,
        lastActivity: serverTimestamp()
      });
      // Navigate back to room lobby
      navigate(`/room/${roomCode}`);
    } catch (error) {
      console.error('Error leaving game:', error);
      // Navigate to room lobby anyway
      navigate(`/room/${roomCode}`);
    }
  };

  if (loading) {
    return (
      <div className="dotsandboxes-container">
        <div className="loading-message">
          <div className="spinner-large"></div>
          <p>Loading game...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dotsandboxes-container">
        <div className="error-message">
          <p>{error}</p>
          <button onClick={handleLeaveGame}>Back to Lobby</button>
        </div>
      </div>
    );
  }

  if (!gameState) {
    return (
      <div className="dotsandboxes-container">
        <div className="error-message">
          <p>Game not found or not yet created</p>
          <p>Please wait a moment or return to lobby</p>
          <button onClick={handleLeaveGame}>Back to Lobby</button>
        </div>
      </div>
    );
  }

  const isMyTurn = gameState.currentTurn === user.uid;
  const myScore = isHost ? gameState.hostScore : gameState.guestScore;
  const opponentScore = isHost ? gameState.guestScore : gameState.hostScore;
  const iWon = gameState.isGameOver && gameState.winner === user.uid;
  const iLost = gameState.isGameOver && gameState.winner === opponent.userId;
  const isDraw = gameState.isGameOver && gameState.winner === 'draw';

  return (
    <div className="dotsandboxes-container">
      {/* Header */}
      <div className="game-header">
        <h1 className="game-title">📦 Dots & Boxes</h1>
      </div>

      {/* Players Info & Scores */}
      <div className="players-info">
        <div className={`player-card ${isMyTurn && !gameState.isGameOver ? 'active' : ''}`} data-player={isHost ? 'host' : 'guest'}>
          <img src={safeString(user?.photoURL, '')} alt={safeString(user?.displayName, 'You')} className="player-avatar" />
          <div className="player-details">
            <p className="player-name">You</p>
            <p className="player-score">{myScore} boxes</p>
          </div>
        </div>

        <div className="vs-divider">VS</div>

        <div className={`player-card ${!isMyTurn && !gameState.isGameOver ? 'active' : ''}`} data-player={isHost ? 'guest' : 'host'}>
          <img src={safeString(opponent?.photoURL, '')} alt={safeString(opponent?.displayName, 'Opponent')} className="player-avatar" />
          <div className="player-details">
            <p className="player-name">{safeString(opponent?.displayName, 'Opponent')}</p>
            <p className="player-score">{opponentScore} boxes</p>
          </div>
        </div>
      </div>

      {/* Turn Indicator */}
      {!gameState.isGameOver && (
        <div className="turn-indicator-section">
          <p className="turn-indicator">
            {isMyTurn ? "Your Turn!" : `${safeString(opponent?.displayName, 'Opponent')}'s Turn`}
          </p>
        </div>
      )}

      {/* Game Status */}
      {!gameState.isGameOver && (
        <div className="game-status">
          <p className="status-text">
            {isMyTurn ? "Draw a line to claim boxes!" : `Waiting for ${safeString(opponent?.displayName, 'Opponent')}...`}
          </p>
        </div>
      )}

      {/* Game Board */}
      <DotsAndBoxesBoard
        gameState={gameState}
        onLineClick={handleLineClick}
        disabled={!isMyTurn || gameState.isGameOver}
        currentUserId={user.uid}
        hostUser={roomData.host}
        guestUser={roomData.guest}
      />

      {/* Game Over */}
      {gameState.isGameOver && (
        <div className="game-over">
          <div className={`result-message ${iWon ? 'won' : iLost ? 'lost' : 'draw'}`}>
            {isDraw ? (
              <>
                <span className="result-icon">🤝</span>
                <h2>It's a Draw!</h2>
                <p>Great game! Both scored {myScore} boxes!</p>
              </>
            ) : iWon ? (
              <>
                <span className="result-icon">🎉</span>
                <h2>You Won!</h2>
                <p>Congratulations! You claimed {myScore} boxes!</p>
              </>
            ) : (
              <>
                <span className="result-icon">😞</span>
                <h2>You Lost</h2>
                <p>Better luck next time! Final score: {myScore}-{opponentScore}</p>
              </>
            )}
          </div>

          {/* Rematch Section */}
          <div className="rematch-section">
            {!rematchRequested && !opponentRematchRequested && (
              <button className="rematch-button" onClick={handleRematch}>
                🔄 Request Rematch
              </button>
            )}

            {rematchRequested && !opponentRematchRequested && (
              <div className="rematch-status">
                <p>⏳ Waiting for {safeString(opponent?.displayName, 'Opponent')} to accept rematch...</p>
              </div>
            )}

            {!rematchRequested && opponentRematchRequested && (
              <div className="rematch-status">
                <p>🎮 {safeString(opponent?.displayName, 'Opponent')} wants a rematch!</p>
                <button className="rematch-button accept" onClick={handleRematch}>
                  ✓ Accept Rematch
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Move Counter */}
      <div className="move-counter">
        <p>Moves: {gameState.moveCount}</p>
      </div>

      {/* Leave Game Button - Always Visible */}
      <div className="leave-game-section">
        <button className="leave-button" onClick={handleLeaveGame}>
          Leave Game
        </button>
      </div>
    </div>
  );
};

export default DotsAndBoxes;
